import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { DocmanFolderRest } from './docman.service.folder';

@Injectable()
export class DocmanFolderModel {

  public folders: any[] = [];
  private project: any;

  constructor(private docmanFolderRest: DocmanFolderRest) {}

  createRootNode(): any {
    return {
      id: 0,
      title: 'Docs',
      nodes: [],
      isPublic: true,
      parentFolderId: -1,
      isRoot: true,
      isSet: false,
      level: 0,
      collapsed: true
    };
  }

  createFolderNode(folder: any, isRoot: boolean, level: number): any {
    return {
      id: folder.id,
      title: isRoot ? 'Docs' : folder.folderName,
      nodes: [],
      isPublic: folder.isPublic === 'Y',
      parentFolderId: isRoot ? -1 : folder.parentFolderId,
      isRoot: isRoot,
      isSet: folder.isSet,
      level: level,
      collapsed: true,
      isMonitored: folder.isMonitored,
      folder: folder
    };
  }

  createFolderSubTree(parentFolderId: number, folders: any[], level: number, folderPath: any[]): any[] {
    const nodes: any[] = [];
    const children = _.filter(folders, { parentFolderId });

    children.forEach((folder: any) => {
      const _folderPath = [...folderPath];
      const node: any = this.createFolderNode(folder, false, level);
      _folderPath.push(node);
      node.folderPath = _folderPath;
      node.name = node.title;
      node.nodes = this.createFolderSubTree(folder.id, folders, level + 1, _folderPath);
      node.children = node.nodes;
      nodes.push(node);
    });

    return _.sortBy(nodes, (node: any) => node.title.toLowerCase());
  }

  removeFolder(folderId: number): any[] {
    return _.remove(this.folders, { id: folderId });
  }

  getSubTree(folderId: number, node: any): any {
    if (!node) {
      return null;
    }

    if (_.toString(node?.id) === _.toString(folderId)) {
      return node;
    }

    let result: any = null;
    _.each(node.nodes, (childNode: any) => {
      const res = this.getSubTree(folderId, childNode);
      if (!_.isEmpty(res)) {
        result = res;
      }
    });

    return result;
  }

  getFolderSubTree(folderId: number, nodes: any[]): any {
    if (!Array.isArray(nodes) || !nodes?.length) {
      return null;
    }

    return this.getSubTree(folderId, nodes[0]);
  }

  createFolderTree(project: any): any {
    const _folders = _.cloneDeep(this.folders);
    let rootNode: any;

    const rootFolder = _.remove(_folders, { folderName: 'incoming' });

    if (rootFolder?.length > 0) {
      rootNode = this.createFolderNode(rootFolder[0], true, 0);
    } else {
      rootNode = this.createRootNode();
    }

    rootNode.title = project.name;
    rootNode.name = project.name;
    rootNode.folderPath = [rootNode];
    rootNode.children = this.createFolderSubTree(0, _folders, 1, rootNode.folderPath);
    rootNode.nodes = rootNode.children;
    return rootNode;
  }

  setFolders(folders: any[]): void {
    this.folders = folders;
  }

  getFolders(project: any, reload: boolean = false): Promise<any[]> {
    if (!reload && this.folders?.length > 0 && this.project === project) {
      return Promise.resolve(this.folders);
    }

    this.project = project;

    return this.docmanFolderRest.getAll(project.id)
      .then((response: any) => {
        this.folders = _.map(_.get(response, 'items', []), (folder: any) => {
          folder.isPublic = ['Y', true, 1, 'true'].includes(folder.isPublic);
          return folder;
        });
        return this.folders;
      });
  }

  getFolderTree(project: any, reload: boolean = false): Promise<any> {
    return this.getFolders(project, reload)
      .then(() => this.createFolderTree(project));
  }

  getFolder(folderId: number): Promise<any> {
    return this.docmanFolderRest.getFolder(folderId)
      .then((response: any) => response);
  }

  saveFolder(folder: any, isNew: boolean): Promise<any> {
    const _folder = _.cloneDeep(folder);
    _folder.isPublic = _folder.isPublic ? 'Y' : 'N';

    return this.docmanFolderRest.postFolder(_folder)
      .then((response: any) => {
        if (!_.isEmpty(response)) {
          if (isNew) {
            this.folders.push(response);
          } else {
            _.remove(this.folders, { id: folder.id });
            this.folders.push(response);
          }
        }
        return response;
      });
  }

  deleteFolder(folder: any): Promise<any> {
    return this.docmanFolderRest.deleteFolder(folder.id)
      .then((response: any) => {
        this.removeFolder(folder.id);
        return response;
      });
  }

  getRootId(): any {
    return _.find(this.folders, { folderName: 'incoming', parentFolderId: 0 });
  }

  getFolderFiles(folder: any): any[] {
    let files: any[] = [];

    _.each(folder.files, (file: any) => {
      const fileVersion: any = _.maxBy(file.fileVersions, (item: any) => item.versionNumber);
      if (!_.isEmpty(fileVersion)) {
        fileVersion.docmanFile = file;
        files.push(fileVersion);
      }
    });

    return _.sortBy(files, (file: any) => file.filesystem ? file.filesystem.fileName : '');
  }

  createFolderStructure(id: number, folderName: string, isPublic: boolean, parentFolder: any, projectId: number): any {
    return {
      id: id,
      folderName: folderName,
      isPublic: isPublic,
      parentFolderId: typeof parentFolder === 'object' ? _.get(parentFolder, 'id', 0) : parentFolder,
      parentFolderName: _.get(parentFolder, 'title', ''),
      project: projectId
    };
  }

  getFolderOrganizerList(fileFolders: any[], project: any): any {
    _.each(this.folders, (folder: any) => {
      folder.isSet = !_.isEmpty(_.find(fileFolders, { id: folder.id }));
    });

    return this.createFolderTree(project);
  }

  validateFolder(folder: any): any {
    const errors: any = {};

    if (folder.folderName.indexOf('/') > -1) {
      errors.folderName = 'Folder names can\'t contain slashes';
    }

    return errors;
  }
}
